import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface LangToggleProps {
  variant?: 'light' | 'dark';
}

const LANGUAGES = [
  { code: 'zh-Hant', key: 'lang.zh-Hant' },
  { code: 'zh-Hans', key: 'lang.zh-Hans' },
  { code: 'en', key: 'lang.en' },
  { code: 'ja', key: 'lang.ja' },
  { code: 'ko', key: 'lang.ko' },
];

export default function LangToggle({ variant = 'light' }: LangToggleProps) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLang = LANGUAGES.find((l) => i18n.language.startsWith(l.code.split('-')[0]) && (!l.code.includes('-') || i18n.language === l.code)) || LANGUAGES[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const isLight = variant === 'light';

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full transition-all whitespace-nowrap cursor-pointer font-medium border ${
          open
            ? isLight
              ? 'bg-background-50/30 border-background-50/50 text-background-50'
              : 'bg-background-100 border-foreground-300 text-foreground-900'
            : isLight
              ? 'border-background-50/30 bg-background-950/20 backdrop-blur-md text-background-50'
              : 'border-foreground-200 bg-background-50 text-foreground-700 hover:text-foreground-900'
        }`}
      >
        <span>{t(currentLang.key)}</span>
        <i
          className={`ri-arrow-down-s-line text-xs w-3 h-3 flex items-center justify-center transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className={`absolute top-full mt-2 max-md:left-0 md:right-0 rounded-xl border py-1 min-w-[120px] z-50 shadow-lg ${
            isLight
              ? 'bg-foreground-950/95 backdrop-blur-xl border-background-50/20'
              : 'bg-background-50 border-background-200/70'
          }`}
        >
          {LANGUAGES.map((lng) => {
            const isActive = currentLang.code === lng.code;
            return (
              <button
                key={lng.code}
                onClick={() => handleSelect(lng.code)}
                className={`w-full text-left px-4 py-2 text-xs transition-colors whitespace-nowrap cursor-pointer font-medium ${
                  isActive
                    ? isLight
                      ? 'text-primary-500 bg-background-50/10'
                      : 'text-primary-600 bg-background-100'
                    : isLight
                      ? 'text-background-100 hover:text-primary-500 hover:bg-background-50/10'
                      : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                }`}
              >
                {t(lng.key)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}